import { useEffect, useMemo, useState } from 'react';

import AppLogger from '../../../../common/logger/AppLogger';
import {
  useEndGameLineItemMutation,
  useGetGameMenuItemQuery,
  useLazyGetGameLineItemStatusQuery,
  useMoveGameLineItemMutation,
} from '../../../api/menu/menuSlice';
import { Transaction } from '../../../api/transaction/Transaction';
import { TransactionLineItem } from '../../../api/transactionLineItem/TransactionLineItem';

import { getAvailabilityInfo } from './gameAvailability';
import { findOpenGameLineItem } from './openGameLineItems';

import './end-game-modal.sass';

interface EndGameModalProps {
  transaction: Transaction;
  onAllEnded?: () => void;
  onSkip?: () => void;
}

function EndGameModal({ transaction, onAllEnded, onSkip }: EndGameModalProps) {
  const outstandingLine = useMemo<TransactionLineItem | undefined>(
    () => findOpenGameLineItem(transaction.transactionLineItems),
    [transaction],
  );

  const [fetchStatus, { data: status, isFetching: statusFetching }] = useLazyGetGameLineItemStatusQuery();
  const [endGame, { isLoading: isEnding }] = useEndGameLineItemMutation();
  const [moveGame, { isLoading: isMoving }] = useMoveGameLineItemMutation();
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<'main' | 'move'>('main');

  const { data: gameMenuItem, isFetching: tablesFetching } = useGetGameMenuItemQuery(outstandingLine?.menuItemId ?? 0, {
    skip: !outstandingLine || view !== 'move',
  });

  useEffect(() => {
    if (outstandingLine) {
      fetchStatus(outstandingLine.id)
        .unwrap()
        .catch((err) => AppLogger.warn('Failed to fetch game status', err));
    } else if (onAllEnded) onAllEnded();
  }, [outstandingLine, fetchStatus, onAllEnded]);

  if (!outstandingLine) return null;

  const minutesSoFar = status ? status.elapsedMinutes : null;

  const handleEnd = async () => {
    setError(null);
    try {
      await endGame(outstandingLine.id).unwrap();
    } catch (err) {
      const message = typeof err === 'object' && err !== null && 'data' in err ? String(err.data) : String(err);
      setError(message);
    }
  };

  const handleMove = async (newInstanceId: number) => {
    setError(null);
    try {
      const result = await moveGame({ lineItemId: outstandingLine.id, newInstanceId }).unwrap();
      if (!result.success) {
        setError(result.errorMessage ?? 'Move failed');
        return;
      }
      setView('main');
    } catch (err) {
      const message = typeof err === 'object' && err !== null && 'data' in err ? String(err.data) : String(err);
      setError(message);
    }
  };

  const moveTargets =
    gameMenuItem?.instances
      .filter((i) => i.isSellable && i.instanceId !== outstandingLine.gameInstanceId)
      .map((i) => ({ instance: i, availability: getAvailabilityInfo(i) }))
      .filter(({ availability }) => availability.available) ?? [];
  const busy = isEnding || isMoving;

  return (
    <div className="pos-end-game-modal-portal">
      <div className="pos-end-game-modal-overlay">
        <div className="pos-end-game-modal">
          <h2 className="pos-end-game-modal__title">{view === 'move' ? 'Move to Table' : 'Finalise Game'}</h2>
          <div className="pos-end-game-modal__body">
            <div className="pos-end-game-modal__row">
              <span className="pos-end-game-modal__label">Item</span>
              <span className="pos-end-game-modal__value">{outstandingLine.description}</span>
            </div>
            {outstandingLine.gameCustomerName && (
              <div className="pos-end-game-modal__row">
                <span className="pos-end-game-modal__label">Customer</span>
                <span className="pos-end-game-modal__value">{outstandingLine.gameCustomerName}</span>
              </div>
            )}
            <div className="pos-end-game-modal__row">
              <span className="pos-end-game-modal__label">Time so far</span>
              <span className="pos-end-game-modal__value">
                {statusFetching ? '…' : minutesSoFar !== null ? `${minutesSoFar} min` : 'Unknown'}
              </span>
            </div>
            {view === 'move' && (
              <div className="pos-end-game-modal__tables">
                {tablesFetching && <div className="pos-end-game-modal__row">Loading tables…</div>}
                {!tablesFetching && moveTargets.length === 0 && <div className="pos-end-game-modal__row">No other tables available.</div>}
                {moveTargets.map(({ instance, availability }) => {
                  const quantifier = gameMenuItem?.timeQuantifier ?? 'min';
                  const subLabel =
                    availability.timeUntilNextBooking !== undefined
                      ? `${availability.timeUntilNextBooking} ${quantifier}${
                          availability.timeUntilNextBooking !== 1 ? 's' : ''
                        } until next booking`
                      : 'Available';
                  return (
                    <button
                      key={instance.instanceId}
                      className="pos-end-game-modal__table"
                      onClick={() => handleMove(instance.instanceId)}
                      disabled={busy}
                      type="button"
                    >
                      <span className="pos-end-game-modal__table-name">{instance.instanceName}</span>
                      <span className="pos-end-game-modal__table-sub">{subLabel}</span>
                    </button>
                  );
                })}
              </div>
            )}
            {error && <div className="pos-end-game-modal__error">{error}</div>}
          </div>
          <div className="pos-end-game-modal__actions">
            {view === 'main' && (
              <>
                {onSkip && (
                  <button
                    className="pos-end-game-modal__action pos-end-game-modal__action_role_skip"
                    onClick={onSkip}
                    disabled={busy}
                    type="button"
                  >
                    Skip / Keep Going
                  </button>
                )}
                <button
                  className="pos-end-game-modal__action pos-end-game-modal__action_role_move"
                  onClick={() => {
                    setError(null);
                    setView('move');
                  }}
                  disabled={busy}
                  type="button"
                >
                  Move
                </button>
                <button
                  className="pos-end-game-modal__action pos-end-game-modal__action_role_end"
                  onClick={handleEnd}
                  disabled={busy}
                  type="button"
                >
                  {isEnding ? 'Ending…' : 'End Game'}
                </button>
              </>
            )}
            {view === 'move' && (
              <button
                className="pos-end-game-modal__action pos-end-game-modal__action_role_skip"
                onClick={() => {
                  setError(null);
                  setView('main');
                }}
                disabled={busy}
                type="button"
              >
                Back
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EndGameModal;
